// Hopefully the only case of breaking feature isolation.
import { propsSchema as embedPropsSchema } from "@/features/embeds/types";
import { HaloRiskValue } from "@/store/services/facades/types/halo";
import { SimpleStructuralRiskValue } from "@/store/services/facades/types/simple";
import { z } from "zod";
export type AbcdeRiskValue = "A" | "B" | "C" | "D" | "E";
export type WordyRiskValue = HaloRiskValue | SimpleStructuralRiskValue;

/**
 * Monetary values concerning an asset.
 */
export const inputValuesSchema = z.object({
  marketValue: z.number(),
  replacementCost: z.number(),
  mortgageRate: z.number(),
});
export type InputValuesConfig = z.infer<typeof inputValuesSchema>;

export const archetypeCategories = ["residential", "commercial", "industrial"] as const;
export type ArchetypeCategory = (typeof archetypeCategories)[number];

const archetypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  value: z.string(),
  /** The value you'd like to send to APIs, if it differs from the Value. */
  aliasOf: z.string().optional(),
  defaultInputValues: inputValuesSchema.optional(),
  category: z.enum(archetypeCategories),
  description: z.string().optional(),
  defaultBuildYear: z.number().optional(),
  defaultHeightAboveGround: z.number().optional(),
  warning: z.string().optional(),
  service: z.string().optional(),
  embed: embedPropsSchema.optional(),
});
export type ArchetypeConfig = z.infer<typeof archetypeSchema>;

const baseSchema = z.object({
  archetypes: z.array(archetypeSchema),
  quickBatch: z.object({
    maxAddresses: z.number(),
  }),
  maxLetter: z.enum(["A", "B", "C", "D", "E"]),
});

export const abcRiskBandsSchema = baseSchema.extend({
  mode: z.literal("abc"),
});
export type AbcRiskBandsConfig = z.infer<typeof abcRiskBandsSchema>;

export const haloRiskBandsSchema = baseSchema.extend({
  mode: z.literal("halo"),
});
export type HaloRiskBandsConfig = z.infer<typeof haloRiskBandsSchema>;

export const simpleRiskBandsSchema = baseSchema.extend({
  mode: z.literal("simple"),
});
export type SimpleRiskBandsConfig = z.infer<typeof simpleRiskBandsSchema>;

export const riskBandsSchema = z.discriminatedUnion("mode", [
  abcRiskBandsSchema,
  haloRiskBandsSchema,
  simpleRiskBandsSchema,
]);
export type RiskBandsConfig = z.infer<typeof riskBandsSchema>;
